import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend, CartesianGrid
} from 'recharts';
import { motion } from 'motion/react';
import { FactoryManagementPanel } from '../components/cockpit/FactoryManagementPanel';
import { VersionLogPanel } from '../components/cockpit/VersionLogPanel';
import { SubscriptionAnalyticsPanel } from '../components/cockpit/SubscriptionAnalyticsPanel';
import { CockpitAdminPanel } from '../components/cockpit/CockpitAdminPanel';
import { useAuth } from '../contexts/AuthContext';

// Color themes matching the QA MATE Enterprise palette
const ACCENT_BLUE = '#136bce';
const SECONDARY_BLUE = '#0e52a0';
const GREEN = '#10b981';
const RED = '#ef4444';
const AMBER = '#f59e0b';
const SLATE_GRAY = '#64748b';

const PIE_COLORS = [ACCENT_BLUE, '#3b82f6', '#60a5fa', AMBER, RED];

const LogoIcon = ({ className = "h-8 w-8" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 2L91.6 26V74L50 98L8.4 74V26L50 2Z" fill="#136BCE"/>
    <path d="M50 22L74 36L50 50L26 36Z" stroke="white" strokeWidth="8" strokeLinejoin="round"/>
    <path d="M26 36V70L50 84V50" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M74 36V70" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface FactoryData {
  name: string;
  region: string;
  inspections: number;
  usersCount: number;
  storageGB: number;
  appVersion: string;
  outdatedDevices: number;
  status: 'Optimal' | 'Warning' | 'Critical';
  uptime: string;
  lastInspectionLabel: string;
  daysSinceLastInspection: number;
  subscriptionStatus?: 'Active' | 'Trial' | 'Grace' | 'Expired';
  subscriptionStart?: string;
  subscriptionEnd?: string;
  allowedUsers?: number;
}

const FACTORIES: FactoryData[] = [
  {
    name: "Palm Fiber",
    region: "Karur",
    inspections: 284,
    usersCount: 54,
    storageGB: 92.4,
    appVersion: "v1.4.2",
    outdatedDevices: 0,
    status: "Optimal",
    uptime: "99.8%",
    lastInspectionLabel: "2 hours ago",
    daysSinceLastInspection: 0,
    subscriptionStatus: "Active",
    subscriptionStart: "15 Jan 2025",
    subscriptionEnd: "15 Jan 2027",
    allowedUsers: 50
  },
  {
    name: "KKP Textile",
    region: "Coimbatore",
    inspections: 310,
    usersCount: 86, // Factories with most number of users
    storageGB: 110.1,
    appVersion: "v1.4.2",
    outdatedDevices: 1,
    status: "Optimal",
    uptime: "99.5%",
    lastInspectionLabel: "1 day ago",
    daysSinceLastInspection: 1,
    subscriptionStatus: "Active",
    subscriptionStart: "20 Nov 2025",
    subscriptionEnd: "20 Nov 2026",
    allowedUsers: 80
  },
  {
    name: "Jayvel",
    region: "Chennai",
    inspections: 320, // Factory who completed maximum inspections
    usersCount: 42,
    storageGB: 84.6,
    appVersion: "v1.4.2",
    outdatedDevices: 0,
    status: "Optimal",
    uptime: "99.9%",
    lastInspectionLabel: "5 hours ago",
    daysSinceLastInspection: 0,
    subscriptionStatus: "Active",
    subscriptionStart: "18 May 2025",
    subscriptionEnd: "18 May 2027",
    allowedUsers: 45
  },
  {
    name: "Mats & More",
    region: "Bangalore",
    inspections: 195,
    usersCount: 31,
    storageGB: 124.5, // Factories using most data storage
    appVersion: "v1.4.1",
    outdatedDevices: 2,
    status: "Warning",
    uptime: "98.2%",
    lastInspectionLabel: "18 days ago",
    daysSinceLastInspection: 18,
    subscriptionStatus: "Grace",
    subscriptionStart: "04 Aug 2025",
    subscriptionEnd: "15 Jul 2026",
    allowedUsers: 30
  },
  {
    name: "Synergy",
    region: "Karur",
    inspections: 142,
    usersCount: 25,
    storageGB: 45.2,
    appVersion: "v1.3.8",
    outdatedDevices: 4, // Factories with most outdated versions
    status: "Critical",
    uptime: "96.4%",
    lastInspectionLabel: "94 days ago",
    daysSinceLastInspection: 94,
    subscriptionStatus: "Expired",
    subscriptionStart: "30 Mar 2024",
    subscriptionEnd: "30 Mar 2025",
    allowedUsers: 25
  },
  {
    name: "Vellum Garments",
    region: "Salem",
    inspections: 0,
    usersCount: 8,
    storageGB: 0.0,
    appVersion: "v1.3.2",
    outdatedDevices: 1,
    status: "Critical",
    uptime: "0.0%",
    lastInspectionLabel: "Never (No activity)",
    daysSinceLastInspection: 999,
    subscriptionStatus: "Trial",
    subscriptionStart: "10 Jun 2026",
    subscriptionEnd: "25 Jun 2026",
    allowedUsers: 20
  },
  {
    name: "Kalyani Spinners",
    region: "Madurai",
    inspections: 12,
    usersCount: 15,
    storageGB: 3.8,
    appVersion: "v1.3.8",
    outdatedDevices: 3,
    status: "Critical",
    uptime: "88.2%",
    lastInspectionLabel: "148 days ago",
    daysSinceLastInspection: 148,
    subscriptionStatus: "Trial",
    subscriptionStart: "12 Jun 2026",
    subscriptionEnd: "27 Jun 2026",
    allowedUsers: 20
  },
  {
    name: "Apex Poly",
    region: "Tuticorin",
    inspections: 4,
    usersCount: 6,
    storageGB: 1.1,
    appVersion: "v1.3.2",
    outdatedDevices: 2,
    status: "Critical",
    uptime: "90.1%",
    lastInspectionLabel: "210 days ago",
    daysSinceLastInspection: 210,
    subscriptionStatus: "Expired",
    subscriptionStart: "10 May 2024",
    subscriptionEnd: "25 May 2025",
    allowedUsers: 15
  }
];

const menuLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  factories: 'Factory Management',
  users: 'User Management',
  versions: 'App Version Analytics',
  subscriptions: 'Subscription Analytics',
  cockpit_admin: 'Cockpit Admin Management'
};

const AdminCockpit: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeMenu, setActiveMenu] = useState<string>('dashboard');
  const [searchQuery, setSearchQuery] = useState('');

  // Dark mode & Text Sizing Preference States
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    return localStorage.getItem('cockpit_dark_mode') === 'true';
  });
  const [textSize, setTextSize] = useState<'M' | 'L' | 'XL'>(() => {
    return (localStorage.getItem('cockpit_text_size') as 'M' | 'L' | 'XL') || 'M';
  });

  useEffect(() => {
    localStorage.setItem('cockpit_dark_mode', String(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('cockpit_text_size', textSize);
    const html = document.documentElement;
    const originalSize = html.style.fontSize;
    
    if (textSize === 'M') {
      html.style.fontSize = '17px';
    } else if (textSize === 'L') {
      html.style.fontSize = '21px';
    } else if (textSize === 'XL') {
      html.style.fontSize = '25px';
    }
    
    return () => {
      html.style.fontSize = originalSize || '18.5px';
    };
  }, [textSize]);
  
  // Exit to standard app or login screen
  const handleExitCockpit = async () => {
    document.documentElement.style.fontSize = '18.5px';
    await logout();
    navigate('/login', { replace: true });
  };

  // Pie chart structure for App Version Share
  const versionShareData = [
    { name: 'v1.4.2 (Latest)', value: 182, color: GREEN },
    { name: 'v1.4.1 (Min Required)', value: 31, color: ACCENT_BLUE },
    { name: 'v1.3.8 (Legacy)', value: 12, color: AMBER },
    { name: 'v1.3.2 (Outdated)', value: 7, color: RED },
  ];

  return (
    <div className={`min-h-[100dvh] bg-slate-50 flex flex-col font-sans admin-cockpit-container ${darkMode ? 'dark-mode' : ''}`}>
      {/* Dynamic Style injection for premium Dark Mode overrides on all admin screens */}
      <style>{`
        .admin-cockpit-container.dark-mode {
          background-color: #0d1222 !important;
          color: #f1f5f9 !important;
        }
        .admin-cockpit-container.dark-mode header {
          background-color: #111827 !important;
          border-color: #1f2937 !important;
        }
        .admin-cockpit-container.dark-mode header div {
          border-color: #1f2937 !important;
        }
        .admin-cockpit-container.dark-mode header span {
          color: #e5e7eb !important;
        }
        .admin-cockpit-container.dark-mode header .text-\\[11px\\],
        .admin-cockpit-container.dark-mode header .text-slate-500 {
          color: #9ca3af !important;
        }
        .admin-cockpit-container.dark-mode header nav button {
          color: #9ca3af !important;
        }
        .admin-cockpit-container.dark-mode header nav button:hover {
          color: #ffffff !important;
        }
        .admin-cockpit-container.dark-mode header nav button.border-transparent {
          border-color: transparent !important;
        }
        .admin-cockpit-container.dark-mode header nav button:not(.border-transparent) {
          border-color: #136bce !important;
          color: #3b82f6 !important;
        }
        .admin-cockpit-container.dark-mode .bg-white {
          background-color: #151d30 !important;
          color: #f1f5f9 !important;
          border-color: #242f47 !important;
        }
        .admin-cockpit-container.dark-mode .bg-slate-50 {
          background-color: #1a2333 !important;
          color: #e2e8f0 !important;
        }
        .admin-cockpit-container.dark-mode .text-slate-900,
        .admin-cockpit-container.dark-mode .text-slate-800,
        .admin-cockpit-container.dark-mode .text-slate-950,
        .admin-cockpit-container.dark-mode h1,
        .admin-cockpit-container.dark-mode h2,
        .admin-cockpit-container.dark-mode h3,
        .admin-cockpit-container.dark-mode h4,
        .admin-cockpit-container.dark-mode .text-slate-650,
        .admin-cockpit-container.dark-mode .text-slate-705,
        .admin-cockpit-container.dark-mode .text-slate-700,
        .admin-cockpit-container.dark-mode .text-slate-600 {
          color: #f8fafc !important;
        }
        .admin-cockpit-container.dark-mode .text-slate-500,
        .admin-cockpit-container.dark-mode .text-slate-400 {
          color: #94a3b8 !important;
        }
        .admin-cockpit-container.dark-mode .text-slate-550 {
          color: #cbd5e1 !important;
        }
        .admin-cockpit-container.dark-mode .border,
        .admin-cockpit-container.dark-mode .border-slate-100,
        .admin-cockpit-container.dark-mode .border-slate-150,
        .admin-cockpit-container.dark-mode .border-slate-200,
        .admin-cockpit-container.dark-mode .border-slate-200\\/50,
        .admin-cockpit-container.dark-mode .border-slate-200\\/80,
        .admin-cockpit-container.dark-mode .border-slate-300,
        .admin-cockpit-container.dark-mode th,
        .admin-cockpit-container.dark-mode td {
          border-color: #242f47 !important;
        }
        .admin-cockpit-container.dark-mode input,
        .admin-cockpit-container.dark-mode select,
        .admin-cockpit-container.dark-mode textarea {
          background-color: #0b1120 !important;
          color: #f8fafc !important;
          border-color: #1e293b !important;
        }
        .admin-cockpit-container.dark-mode input::placeholder {
          color: #475569 !important;
        }
        .admin-cockpit-container.dark-mode .bg-slate-100,
        .admin-cockpit-container.dark-mode .bg-slate-150 {
          background-color: #0e1626 !important;
          border-color: #1e293b !important;
        }
        .admin-cockpit-container.dark-mode .bg-slate-50\\/40,
        .admin-cockpit-container.dark-mode .bg-slate-50\\/50,
        .admin-cockpit-container.dark-mode .bg-slate-50\\/70,
        .admin-cockpit-container.dark-mode .bg-slate-100\\/70 {
          background-color: rgba(22, 30, 49, 0.65) !important;
          border-color: #242f47 !important;
        }
        .admin-cockpit-container.dark-mode .hover\\:bg-slate-50:hover,
        .admin-cockpit-container.dark-mode .hover\\:bg-slate-50\\/80:hover,
        .admin-cockpit-container.dark-mode tr:hover {
          background-color: #1d283f !important;
        }
        .admin-cockpit-container.dark-mode .bg-blue-50,
        .admin-cockpit-container.dark-mode .bg-blue-50\\/20,
        .admin-cockpit-container.dark-mode .bg-blue-50\\/60,
        .admin-cockpit-container.dark-mode .bg-blue-50\\/70 {
          background-color: rgba(59, 130, 246, 0.18) !important;
          color: #60a5fa !important;
          border-color: rgba(59, 130, 246, 0.45) !important;
        }
        .admin-cockpit-container.dark-mode .bg-emerald-50,
        .admin-cockpit-container.dark-mode .bg-emerald-50\\/70 {
          background-color: rgba(16, 185, 129, 0.18) !important;
          color: #34d399 !important;
          border-color: rgba(16, 185, 129, 0.4) !important;
        }
        .admin-cockpit-container.dark-mode .bg-amber-50,
        .admin-cockpit-container.dark-mode .bg-amber-50\\/70 {
          background-color: rgba(245, 158, 11, 0.18) !important;
          color: #fbbf24 !important;
          border-color: rgba(245, 158, 11, 0.4) !important;
        }
        .admin-cockpit-container.dark-mode .bg-rose-50,
        .admin-cockpit-container.dark-mode .bg-rose-50\\/70,
        .admin-cockpit-container.dark-mode .bg-red-50 {
          background-color: rgba(239, 68, 68, 0.18) !important;
          color: #f87171 !important;
          border-color: rgba(239, 68, 68, 0.4) !important;
        }
        .admin-cockpit-container.dark-mode .bg-red-50\\/20 {
          background-color: rgba(239, 68, 68, 0.15) !important;
          border-color: rgba(239, 68, 68, 0.25) !important;
        }
        .admin-cockpit-container.dark-mode .hover\\:bg-red-50\\/40:hover {
          background-color: rgba(239, 68, 68, 0.25) !important;
        }
        .admin-cockpit-container.dark-mode .hover\\:bg-slate-50\\/50:hover {
          background-color: rgba(255, 255, 255, 0.05) !important;
        }
        .admin-cockpit-container.dark-mode .bg-blue-150,
        .admin-cockpit-container.dark-mode .bg-blue-50\\/80 {
          background-color: rgba(59, 130, 246, 0.22) !important;
          color: #60a5fa !important;
          border-color: rgba(59, 130, 246, 0.45) !important;
        }
        .admin-cockpit-container.dark-mode .hover\\:bg-blue-50:hover {
          background-color: rgba(59, 130, 246, 0.25) !important;
          color: #60a5fa !important;
        }
        .admin-cockpit-container.dark-mode .hover\\:bg-blue-100:hover {
          background-color: rgba(59, 130, 246, 0.35) !important;
          color: #ffffff !important;
        }
        .admin-cockpit-container.dark-mode .ring-blue-100,
        .admin-cockpit-container.dark-mode .ring-blue-200,
        .admin-cockpit-container.dark-mode .ring-blue-100\\/40,
        .admin-cockpit-container.dark-mode .ring-\\[\\#136bce\\]\\/20,
        .admin-cockpit-container.dark-mode .ring-\\[\\#136BCE\\]\\/20 {
          --tw-ring-color: rgba(59, 130, 246, 0.35) !important;
        }
        .admin-cockpit-container.dark-mode .text-\\[\\#136bce\\],
        .admin-cockpit-container.dark-mode .text-\\[\\#136BCE\\],
        .admin-cockpit-container.dark-mode .text-blue-700 {
          color: #60a5fa !important;
        }
        .admin-cockpit-container.dark-mode .text-emerald-600,
        .admin-cockpit-container.dark-mode .text-emerald-700 {
          color: #34d399 !important;
        }
        .admin-cockpit-container.dark-mode .text-amber-700 {
          color: #fbbf24 !important;
        }
        .admin-cockpit-container.dark-mode .text-rose-700,
        .admin-cockpit-container.dark-mode .text-red-700,
        .admin-cockpit-container.dark-mode .text-red-650,
        .admin-cockpit-container.dark-mode .text-red-600 {
          color: #fca5a5 !important;
        }
        .admin-cockpit-container.dark-mode .border-blue-200 {
          border-color: rgba(59, 130, 246, 0.35) !important;
        }
        .admin-cockpit-container.dark-mode .border-emerald-200,
        .admin-cockpit-container.dark-mode .border-emerald-100 {
          border-color: rgba(16, 185, 129, 0.3) !important;
        }
        .admin-cockpit-container.dark-mode .border-amber-200 {
          border-color: rgba(245, 158, 11, 0.3) !important;
        }
        .admin-cockpit-container.dark-mode .border-rose-200,
        .admin-cockpit-container.dark-mode .border-rose-100,
        .admin-cockpit-container.dark-mode .border-red-200,
        .admin-cockpit-container.dark-mode .border-red-100 {
          border-color: rgba(239, 68, 68, 0.3) !important;
        }
        .admin-cockpit-container.dark-mode .bg-blue-100 {
          background-color: rgba(59, 130, 246, 0.2) !important;
          color: #60a5fa !important;
        }
        .admin-cockpit-container.dark-mode .bg-purple-100 {
          background-color: rgba(168, 85, 247, 0.2) !important;
          color: #c084fc !important;
        }
        .admin-cockpit-container.dark-mode .text-purple-700 {
          color: #c084fc !important;
        }
        .admin-cockpit-container.dark-mode .bg-amber-100 {
          background-color: rgba(245, 158, 11, 0.2) !important;
          color: #fbbf24 !important;
        }
        .admin-cockpit-container.dark-mode .bg-rose-100 {
          background-color: rgba(244, 63, 94, 0.2) !important;
          color: #fb7185 !important;
        }
        .admin-cockpit-container.dark-mode .bg-red-100 {
          background-color: rgba(239, 68, 68, 0.2) !important;
          color: #fca5a5 !important;
        }
        .admin-cockpit-container.dark-mode .hover\\:bg-red-200:hover {
          background-color: rgba(239, 68, 68, 0.35) !important;
        }
      `}</style>

      {/* Consistent Premium White Header */}
      <header className="flex-none z-30 flex flex-col bg-white shadow-sm transition-all duration-300 shrink-0">
        <div className="flex h-16 items-center justify-between border-b border-slate-100 px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <LogoIcon className="h-10 w-10 animate-pulse-subtle" />
              <div className="flex flex-col">
                <span className="text-xl font-bold tracking-tight text-[#136BCE] leading-none text-nowrap">QA MATE</span>
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mt-0.5 text-nowrap">Admin Cockpit</span>
              </div>
            </div>
          </div>

          {/* Quick Info & Exit */}
          <div className="flex items-center gap-4">
            
            {/* Header Accessibility Options for Dark Mode and Text Sizing */}
            <div className="flex items-center gap-3 border-r border-slate-200 pr-4">
              {/* Text Sizing Change */}
              <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-xl border border-slate-200/60" title="Adjust Text Size">
                {(['M', 'L', 'XL'] as const).map((sz) => (
                  <button
                    key={sz}
                    onClick={() => setTextSize(sz)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all duration-250 ${
                      textSize === sz
                        ? 'bg-[#136bce] text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    {sz}
                  </button>
                ))}
              </div>

              {/* Dark Mode Toggle Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-1.5 rounded-xl border transition-all duration-200 flex items-center justify-center ${
                  darkMode
                    ? 'bg-amber-500 border-amber-600 text-white shadow-sm'
                    : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                }`}
                title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                <span className="material-symbols-outlined text-[16px]">
                  {darkMode ? 'light_mode' : 'dark_mode'}
                </span>
              </button>
            </div>

            <div className="hidden md:flex flex-col items-end text-right border-r border-slate-200 pr-4">
              <span className="text-xs font-bold text-slate-800 uppercase tracking-widest leading-none mb-1">Master Admin</span>
              <span className="text-[10px] font-mono text-[#136bce] font-semibold">sys-admin@qamate.ai</span>
            </div>
            
            <button 
              onClick={handleExitCockpit}
              className="flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-red-50 text-slate-700 hover:text-red-700 border border-slate-200 hover:border-red-200 rounded-xl text-xs font-bold transition-all"
            >
              <span className="material-symbols-outlined text-sm">logout</span>
              Exit Cockpit
            </button>
          </div>
        </div>

        {/* Navigation Menu matched with standard main layout style */}
        <div className="hidden md:block px-6 lg:px-10 border-b border-slate-100">
          <nav className="flex space-x-4 lg:space-x-8 overflow-visible">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
              { id: 'factories', label: 'Factory Management', icon: 'factory' },
              { id: 'versions', label: 'App Version Analytics', icon: 'terminal' },
              { id: 'subscriptions', label: 'Subscription Analytics', icon: 'workspace_premium' },
              { id: 'cockpit_admin', label: 'Cockpit Admin Management', icon: 'admin_panel_settings' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'dashboard') {
                    setActiveMenu('dashboard');
                  } else {
                    setActiveMenu(item.id);
                  }
                }}
                className={`group flex items-center gap-2 border-b-[3px] py-4 text-sm font-medium transition-all focus:outline-none whitespace-nowrap leading-none ${
                  activeMenu === item.id
                    ? 'border-[#136bce] text-[#136bce]'
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`}
              >
                <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Body */}
      <main className="flex-1 max-w-[1600px] w-full mx-auto p-6 lg:p-10 flex flex-col gap-8">
        {activeMenu === 'dashboard' && (
          /* Executive Page Title matched with 'Executive Quality Intelligence' dashboard style */
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-2 mt-2">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">QA MATE Enterprise Control Center</h1>
              <p className="text-sm text-slate-500 mt-1">Centralized operational oversight, factory node states, and device health metrics.</p>
            </div>
          </div>
        )}
        
        {activeMenu === 'factories' ? (
          <FactoryManagementPanel />
        ) : activeMenu === 'versions' ? (
          <VersionLogPanel />
        ) : activeMenu === 'subscriptions' ? (
          <SubscriptionAnalyticsPanel factories={FACTORIES} />
        ) : activeMenu === 'cockpit_admin' ? (
          <CockpitAdminPanel />
        ) : activeMenu !== 'dashboard' ? (
          /* Locked tab view */
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-12 border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center max-w-2xl mx-auto my-12"
          >
            <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center text-amber-500 mb-6 border border-amber-100">
              <span className="material-symbols-outlined text-[42px]">lock</span>
            </div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Feature Slated for {menuLabels[activeMenu] || activeMenu}</h2>
            <p className="text-sm text-slate-500 max-w-md leading-relaxed mb-8">
              Under current clearance guidelines, the {menuLabels[activeMenu] || activeMenu} cockpit panel is slated for full system integration in release <strong className="text-slate-800">v1.5.0-Enterprise</strong>.
            </p>
            <button 
              onClick={() => setActiveMenu('dashboard')}
              className="px-6 py-3 bg-[#136bce] text-white rounded-xl text-xs font-bold hover:bg-[#115fb8] tracking-widest uppercase transition-all flex items-center gap-2 shadow-sm"
            >
              <span className="material-symbols-outlined text-sm">arrow_back</span>
              Return to Admin Dashboard
            </button>
          </motion.div>
        ) : (
          /* Dashboard tab */
          <>
            {/* Top Stats Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { 
                  title: "Total Factories", 
                  value: "32", 
                  sub: "24 Active, 8 Standby", 
                  icon: "factory", 
                  colorClass: "bg-blue-50 text-blue-600 border-blue-100", 
                  sparklineColor: "#3b82f6" 
                },
                { 
                  title: "Most number of Users", 
                  value: "KKP Textile", 
                  sub: "86 registered staff users", 
                  icon: "groups", 
                  colorClass: "bg-emerald-50 text-emerald-600 border-emerald-100", 
                  sparklineColor: "#10b981" 
                },
                { 
                  title: "Peak Inspections", 
                  value: "Jayvel", 
                  sub: "320 completed inspections", 
                  icon: "checklist_rtl", 
                  colorClass: "bg-indigo-50 text-indigo-600 border-indigo-100",
                  sparklineColor: "#6366f1"
                },
                { 
                  title: "Most Active Inspector", 
                  value: "Prabhakaran", 
                  sub: "184 Completed | Palm Fiber", 
                  icon: "verified_user", 
                  colorClass: "bg-purple-50 text-purple-600 border-purple-100",
                  sparklineColor: "#a855f7"
                },
              ].map((kpi, idx) => (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  key={idx}
                  className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col justify-between group hover:border-[#136bce] transition-all cursor-default"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${kpi.colorClass}`}>
                      <span className="material-symbols-outlined text-[24px]">{kpi.icon}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-black text-slate-900 tracking-tight leading-none mb-1">{kpi.value}</h3>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 leading-tight">{kpi.title}</p>
                    <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden mb-3">
                      <div className="h-full rounded-full" style={{ backgroundColor: kpi.sparklineColor, width: '75%' }}></div>
                    </div>
                    <p className="text-[10px] font-bold text-slate-500 flex items-center gap-1.5 mt-1">
                      <span className="material-symbols-outlined text-[14px]" style={{ color: kpi.sparklineColor }}>info</span>
                      <span className="truncate">{kpi.sub}</span>
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Deep Metric Showcases */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {[
                {
                  title: "Factories with Most Users",
                  desc: "Organizations prioritizing active workforce enrollment",
                  icon: "groups",
                  badge: "Leaderboard",
                  badgeColor: "bg-blue-100 text-blue-700",
                  items: FACTORIES.map(f => ({ name: f.name, value: `${f.usersCount} Active Users`, detail: `${f.region}`, isCritical: false })).sort((a,b) => parseInt(b.value) - parseInt(a.value)).slice(0, 5)
                },
                {
                  title: "Factories Using Most Storage",
                  desc: "Durable Cloud persistence usage for PDFs & images",
                  icon: "cloud",
                  badge: "Storage",
                  badgeColor: "bg-purple-100 text-purple-700",
                  items: FACTORIES.map(f => ({ name: f.name, value: `${f.storageGB} GB`, detail: `${Math.round(f.storageGB / 1.5)} files synced`, isCritical: false })).sort((a,b) => parseFloat(b.value) - parseFloat(a.value)).slice(0, 5)
                },
                {
                  title: "Legacy App Device Risk",
                  desc: "Factories requiring device firmware & build updates",
                  icon: "system_update",
                  badge: "Device Health",
                  badgeColor: "bg-amber-100 text-amber-700",
                  items: [...FACTORIES]
                    .sort((a, b) => b.outdatedDevices - a.outdatedDevices)
                    .map(f => ({ 
                      name: f.name, 
                      value: f.outdatedDevices === 0 ? "Optimal" : `${f.outdatedDevices} Outdated`, 
                      detail: f.appVersion,
                      isCritical: f.outdatedDevices > 0
                    })).slice(0, 5)
                },
                {
                  title: "Dormant & Inactive Factories",
                  desc: "Factories with no live inspections recently or ever",
                  icon: "motion_photos_off",
                  badge: "Usage Alert",
                  badgeColor: "bg-rose-100 text-rose-700",
                  items: [...FACTORIES]
                    .filter(f => f.daysSinceLastInspection > 10)
                    .sort((a, b) => b.daysSinceLastInspection - a.daysSinceLastInspection)
                    .map(f => ({
                      name: f.name,
                      value: f.lastInspectionLabel,
                      detail: `${f.inspections} total completions`,
                      isCritical: f.daysSinceLastInspection > 90
                    })).slice(0, 5)
                }
              ].map((block, index) => (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + (index * 0.1) }}
                  key={index}
                  className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[#136bce] text-xl">{block.icon}</span>
                        <h4 className="font-bold text-slate-800 text-sm tracking-wide">{block.title}</h4>
                      </div>
                      {block.badge && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider inline-flex items-center justify-center text-center ${block.badgeColor}`}>
                          {block.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-550 mb-4">{block.desc}</p>
                    <div className="space-y-3">
                      {block.items.map((item: { name: string; value: string; detail: string; isCritical: boolean }, id) => (
                        <div key={id} className="flex items-center justify-between p-3 bg-slate-50/50 hover:bg-slate-50 rounded-xl border border-slate-100 transition-colors">
                          <div className="truncate max-w-[180px] sm:max-w-xs md:max-w-md lg:max-w-[200px] xl:max-w-[260px]">
                            <p className="text-[11px] font-bold text-slate-900 truncate">{item.name}</p>
                            <p className="text-[10px] text-slate-500 truncate">{item.detail}</p>
                          </div>
                          <span className={`text-[11px] font-bold ${
                            item.isCritical ? 'text-red-600 bg-red-50 px-2 py-0.5 rounded-lg border border-red-100' : 'text-slate-800'
                          }`}>
                            {item.value || ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Custom Visual Charts Panel */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Completed Inspections Output */}
              <div className="lg:col-span-2 bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-sm">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-base font-bold text-slate-800">Inspection Capacity Analytics</h3>
                    <p className="text-xs text-slate-400">Yield output & testing volume sorted by factory</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded bg-[#136bce]"></span>
                    <span className="text-[10px] font-bold text-slate-505 uppercase tracking-wide">Inspections Completed</span>
                  </div>
                </div>
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={FACTORIES} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 10, fill: '#64748b' }} 
                        tickFormatter={(v) => v.split(' ')[0]} 
                      />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip 
                        contentStyle={{ 
                          borderRadius: '12px', 
                          border: 'none', 
                          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                          backgroundColor: '#1e293b',
                          color: '#fff'
                        }}
                      />
                      <Bar dataKey="inspections" fill="#136bce" radius={[4, 4, 0, 0]} barSize={35}>
                        {FACTORIES.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.name.includes("Zenith") ? SECONDARY_BLUE : ACCENT_BLUE} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Version & Build Integrity */}
              <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-sm flex flex-col justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-800 mb-1">Device Version Integrity</h3>
                  <p className="text-xs text-slate-400 mb-6">Device client software state overview</p>

                  <div className="h-[180px] w-full flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={versionShareData}
                          innerRadius={45}
                          outerRadius={65}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {versionShareData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t border-slate-100">
                  {versionShareData.map((v, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: v.color }}></span>
                        <span className="text-slate-500 font-medium">{v.name}</span>
                      </div>
                      <span className="font-bold text-slate-800">{v.value} devices</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Comprehensive Factories Master Log */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-slate-850">Factory Analytics</h3>
                  <p className="text-xs text-slate-450 mt-0.5">Real-time status of connected factory nodes and version telemetry</p>
                </div>
                <div className="relative max-w-xs w-full">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">search</span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search factories or regions..."
                    className="w-full bg-slate-50 text-xs rounded-xl border-slate-200 pl-10 pr-4 py-2 text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-1 focus:ring-[#136bce] focus:border-[#136bce] transition-all"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[800px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                      <th className="py-4 px-4">Factory & Region</th>
                      <th className="py-4 px-4">Active Users</th>
                      <th className="py-4 px-4 text-center">Inspections</th>
                      <th className="py-4 px-4 text-center">Last Active / Inspection</th>
                      <th className="py-4 px-4 text-center">Cloud Storage</th>
                      <th className="py-4 px-4">App Version</th>
                      <th className="py-4 px-4 text-center">OUTDATED DEVICES</th>
                      <th className="py-4 px-4 text-right">System Health</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FACTORIES.filter(f => 
                      f.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                      f.region.toLowerCase().includes(searchQuery.toLowerCase())
                    ).map((factory, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50 text-xs text-slate-705 transition-colors">
                        <td className="py-4 px-4 font-bold text-slate-900">
                          <div>{factory.name}</div>
                          <div className="text-[10px] text-slate-400 font-medium mt-0.5">{factory.region}</div>
                        </td>
                        <td className="py-4 px-4 font-semibold text-slate-700">
                          {factory.usersCount} users
                        </td>
                        <td className="py-4 px-4 text-center font-bold text-[#136bce]">
                          {factory.inspections}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${
                            factory.daysSinceLastInspection === 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                            factory.daysSinceLastInspection === 1 ? 'bg-blue-50 text-blue-700 border-blue-100' :
                            factory.daysSinceLastInspection > 90 ? 'bg-red-50 text-red-700 border-red-100' :
                            'bg-amber-50 text-amber-700 border-amber-100'
                          }`}>
                            <span className={`w-1 h-1 rounded-full ${
                              factory.daysSinceLastInspection === 0 ? 'bg-emerald-500' :
                              factory.daysSinceLastInspection === 1 ? 'bg-blue-500' :
                              factory.daysSinceLastInspection > 90 ? 'bg-red-500 animate-pulse' :
                              'bg-amber-500'
                            }`} />
                            {factory.lastInspectionLabel}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center font-semibold text-slate-700">
                          {factory.storageGB} GB
                        </td>
                        <td className="py-4 px-4 text-slate-700 font-semibold">
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-mono">
                            {factory.appVersion}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          {factory.outdatedDevices > 0 ? (
                            <span className="inline-block px-2 py-0.5 bg-red-50 text-red-700 rounded-lg text-[10px] font-bold border border-red-100 font-mono">
                              {factory.outdatedDevices} Device
                            </span>
                          ) : (
                            <span className="text-slate-400 text-[10px] font-semibold">0</span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            factory.status === 'Optimal' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                            factory.status === 'Warning' ? 'bg-amber-50 text-amber-700 border border-amber-100' :
                            'bg-red-50 text-red-700 border border-red-100'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              factory.status === 'Optimal' ? 'bg-emerald-500' :
                              factory.status === 'Warning' ? 'bg-amber-500' :
                              'bg-red-500'
                            }`} />
                            {factory.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Consistent Premium White Footer */}
      <footer className="flex-none border-t border-slate-200 bg-white px-6 py-6 lg:px-10 shrink-0 text-xs mt-auto">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-900">QA MATE</span>
            <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-[#136bce] ring-1 ring-inset ring-blue-700/10 uppercase tracking-tighter">
              Admin Cockpit Core v2.1.0
            </span>
          </div>
          <p className="text-[10px] text-slate-500">
            Secure administrator channel active. All connections are audited. © {new Date().getFullYear()} QA MATE Systems.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AdminCockpit;
